import {
  LOGO_B64,
  SIG_B64,
  COMPANY,
  C_DARK,
  C_GREY_TEXT,
  C_GREY_BG,
  C_WHITE,
} from "./generateInspectionPDF";

const C_NAVY     = [30, 58, 95];
const C_ROW_ALT  = [250, 250, 252];
const C_SUB_BG   = [243, 246, 251];
const C_EFSM_HDR = [210, 222, 238];


export function generateRectificationOverviewPDF(quotes, opts = {}) {
  if (!quotes || quotes.length === 0) return null;

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 15;
  const contentW = pageW - margin * 2;
  let y = margin;
  let pageNum = 1;

  const fmt2 = (n) =>
    Number(n).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const ref = quotes[0];

  function drawPageFooter() {
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(`Page ${pageNum}`, pageW / 2, pageH - 7, { align: "center" });
  }

  function checkPage(needed = 18) {
    if (y + needed > pageH - 25) {
      drawPageFooter();
      doc.addPage();
      pageNum++;
      y = margin;
    }
  }

  // ── HEADER ────────────────────────────────────────────────────────────────
  const logoW = 38;
  const logoH = logoW * (211 / 500);
  doc.addImage("data:image/jpeg;base64," + LOGO_B64, "JPEG", margin, y, logoW, logoH);

  const cx = pageW - margin;
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.name, cx, y + 3, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.accreditation, cx, y + 7.5, { align: "right" });
  doc.text(COMPANY.address, cx, y + 11.5, { align: "right" });
  doc.text(COMPANY.phone, cx, y + 15.5, { align: "right" });
  doc.text(COMPANY.email, cx, y + 19.5, { align: "right" });

  y += logoH + 7;
  doc.setDrawColor(...C_GREY_TEXT);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 8;

  // ── TITLE ─────────────────────────────────────────────────────────────────
  const typeLabels = { annual: "Annual", bi_annual: "Bi Annual", monthly: "Monthly", six_monthly: "Six Monthly" };
  const inspYear = ref.inspection_date ? new Date(ref.inspection_date).getFullYear() : new Date().getFullYear();
  const inspLabel = `${typeLabels[ref.inspection_type] || (ref.inspection_type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} Inspection ${inspYear}`;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_NAVY);
  doc.text("Rectifications Summary Quotation", pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(inspLabel, pageW / 2, y, { align: "center" });
  y += 7;

  // ── DETAILS BOX ───────────────────────────────────────────────────────────
  doc.setFillColor(...C_GREY_BG);
  doc.roundedRect(margin, y, contentW, 28, 2, 2, "F");
  const col1 = margin + 3;
  const col2 = margin + contentW / 2 + 3;
  const labelH = 4;

  const detailRows = [
    ["Site Name", ref.inspection_site_name || "", "Quote Date", new Date().toLocaleDateString("en-AU")],
    ["Site Address", ref.inspection_site_address || "", "Inspection Date", ref.inspection_date ? new Date(ref.inspection_date).toLocaleDateString("en-AU") : ""],
    ["Inspection Type", (ref.inspection_type || "").replace(/_/g, "-").replace(/\b\w/g, (c) => c.toUpperCase()), "", ""],
  ];

  let dy = y + 5;
  detailRows.forEach(([l1, v1, l2, v2]) => {
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text(l1.toUpperCase(), col1, dy);
    doc.text(l2.toUpperCase(), col2, dy);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...C_DARK);
    doc.setFontSize(8.5);
    doc.text(String(v1), col1, dy + labelH);
    doc.text(String(v2), col2, dy + labelH);
    dy += 9;
  });
  y += 35;

  // ── BUILD EFSM DATA ───────────────────────────────────────────────────────
  // Column x positions
  const C_ITEM = margin + 4;           // item name, left-aligned
  const C_UNIT = margin + 112;         // unit price, right-aligned
  const C_QTY  = margin + 130;         // qty, centre-aligned
  const C_GST  = margin + 150;         // GST, right-aligned
  const C_AMT  = pageW - margin - 2;   // amount, right-aligned

  const efsmData = quotes
    .map((q) => {
      const efsmName = q.items?.[0]?.inspection_item_detail?.category_name || "Other";
      const total = Number(q.total_ex_gst) || 0;

      const groupMap = {};
      (q.items || []).forEach((item) => {
        const name = (item.proposed_solution || "").trim();
        if (!name) return;
        const price = Number(item.price_ex_gst) || 0;
        if (!groupMap[name]) groupMap[name] = { qty: 0, subtotal: 0 };
        groupMap[name].qty += 1;
        groupMap[name].subtotal += price;
      });
      const groups = Object.entries(groupMap)
        .map(([name, g]) => ({
          name,
          qty: g.qty,
          unitPrice: g.qty > 0 ? g.subtotal / g.qty : 0,
          subtotal: g.subtotal,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      return { efsmName, total, groups };
    })
    .sort((a, b) => a.efsmName.localeCompare(b.efsmName));

  // ── EFSM TABLE ────────────────────────────────────────────────────────────
  // Main table header
  doc.setFillColor(...C_NAVY);
  doc.rect(margin, y, contentW, 11, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_WHITE);
  doc.text("Item", C_ITEM, y + 7);
  doc.text("Unit Price", C_UNIT, y + 7, { align: "right" });
  doc.text("Qty", C_QTY, y + 7, { align: "center" });
  doc.text("GST", C_GST, y + 7, { align: "right" });
  doc.text("Amount", C_AMT, y + 5, { align: "right" });
  doc.text("(ex. GST)", C_AMT, y + 9, { align: "right" });
  y += 13;

  let grandTotal = 0;

  efsmData.forEach((row) => {
    const hasGroups = row.groups.length > 0;
    const blockH = 9 + (hasGroups ? row.groups.length * 7 : 0) + 8 + 4;
    checkPage(blockH);

    // ── EFSM section header (no total on right)
    doc.setFillColor(...C_EFSM_HDR);
    doc.rect(margin, y, contentW, 9, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_DARK);
    doc.text(row.efsmName, C_ITEM, y + 6);
    doc.setDrawColor(180, 200, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 9, margin + contentW, y + 9);
    y += 9;

    // ── Item rows
    if (hasGroups) {
      row.groups.forEach((g, gi) => {
        const itemH = 7;
        if (gi % 2 === 0) {
          doc.setFillColor(...C_WHITE);
        } else {
          doc.setFillColor(...C_SUB_BG);
        }
        doc.rect(margin, y, contentW, itemH, "F");

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...C_DARK);
        doc.text(g.name, C_ITEM, y + 4.8);

        doc.setTextColor(...C_GREY_TEXT);
        doc.text(`$${fmt2(g.unitPrice)}`, C_UNIT, y + 4.8, { align: "right" });
        doc.text(String(g.qty), C_QTY, y + 4.8, { align: "center" });
        doc.text(`$${fmt2(g.subtotal * 0.1)}`, C_GST, y + 4.8, { align: "right" });

        doc.setFont("helvetica", "bold");
        doc.setTextColor(...C_NAVY);
        doc.text(`$${fmt2(g.subtotal)}`, C_AMT, y + 4.8, { align: "right" });

        doc.setDrawColor(225, 230, 238);
        doc.setLineWidth(0.15);
        doc.line(margin, y + itemH, margin + contentW, y + itemH);
        y += itemH;
      });
    }

    // ── EFSM subtotal row
    const subH = 8;
    doc.setFillColor(...C_WHITE);
    doc.rect(margin, y, contentW, subH, "F");
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...C_GREY_TEXT);
    doc.text("SUBTOTAL", C_GST, y + 5.2, { align: "right" });
    doc.setTextColor(...C_NAVY);
    doc.text(`$${fmt2(row.total)}`, C_AMT, y + 5.2, { align: "right" });
    doc.setDrawColor(...C_NAVY);
    doc.setLineWidth(0.4);
    doc.line(margin, y + subH, margin + contentW, y + subH);
    y += subH + 4;

    grandTotal += row.total;
  });

  y += 8;

  // ── GRAND TOTALS ──────────────────────────────────────────────────────────
  checkPage(36);
  const totW = 80;
  const totX = pageW - margin - totW;
  const gst = grandTotal * 0.1;
  const totalIncGst = grandTotal * 1.1;

  const totRows = [
    { label: "Subtotal (ex. GST)", value: `$${fmt2(grandTotal)}`, bold: false },
    { label: "GST (10%)", value: `$${fmt2(gst)}`, bold: false },
    { label: "TOTAL (inc. GST)", value: `$${fmt2(totalIncGst)}`, bold: true },
  ];

  totRows.forEach(({ label, value, bold }) => {
    if (bold) {
      doc.setFillColor(...C_NAVY);
      doc.rect(totX - 2, y - 4, totW + 4, 8, "F");
      doc.setTextColor(...C_WHITE);
    } else {
      doc.setTextColor(...C_DARK);
    }
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(bold ? 9 : 8);
    doc.text(label, totX, y);
    doc.text(value, pageW - margin, y, { align: "right" });
    y += 8;
  });

  y += 2;
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("All amounts are in Australian Dollars. GST is calculated at 10%.", margin, y);
  y += 10;

  // ── SIGNATURE ─────────────────────────────────────────────────────────────
  const sigW = (contentW - 10) / 3;
  const sigImgH = Math.min(sigW * (211 / 500), 20);
  checkPage(sigImgH + 30);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, y, margin + contentW, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_DARK);
  doc.text("Authorised By", margin, y);
  y += 3;

  const sx0 = margin;
  const sx1 = margin + sigW + 5;
  const sx2 = margin + (sigW + 5) * 2;
  const dateStr = new Date().toLocaleDateString("en-AU");
  const sigStartY = y;
  const lineY = sigStartY + sigImgH;

  try {
    doc.addImage("data:image/png;base64," + SIG_B64, "PNG", sx2 + 5, sigStartY, sigW - 5, sigImgH);
  } catch (e) {}

  doc.setDrawColor(180, 180, 180);
  doc.setLineWidth(0.4);
  doc.line(sx0, lineY, sx0 + sigW, lineY);
  doc.line(sx1, lineY, sx1 + sigW, lineY);
  doc.line(sx2, lineY, sx2 + sigW, lineY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...C_DARK);
  doc.text(COMPANY.signatory || "", sx0, lineY - 4);
  doc.text(dateStr, sx1, lineY - 4);

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...C_GREY_TEXT);
  doc.text("AUTHORISED SIGNATORY", sx0, lineY + 4);
  doc.text("DATE", sx1, lineY + 4);
  doc.text("SIGNATURE", sx2, lineY + 4);

  y = lineY + 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...C_GREY_TEXT);
  doc.text(COMPANY.name, sx0, y);
  doc.text(COMPANY.accreditation, sx0, y + 4);

  drawPageFooter();

  const siteName = (ref.inspection_site_name || "site").replace(/[^a-z0-9]/gi, "_");
  if (!opts.noDownload) {
    doc.save(`SUMMARY_QUOTE_${siteName}_${inspYear}.pdf`);
  }

  return doc.output("blob");
}
